import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarCheck, FloppyDisk, Copy, Plus, Trash, CalendarPlus, Prohibit } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const bookingBase = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : '';
const WEEKDAYS = [['1', '2ª'], ['2', '3ª'], ['3', '4ª'], ['4', '5ª'], ['5', '6ª'], ['6', 'Sáb'], ['0', 'Dom']];
const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtExcDate = (iso) => { const d = new Date(iso + 'T00:00:00'); return `${d.getDate()} ${MONTHS_PT[d.getMonth()]} ${d.getFullYear()}`; };

function ExceptionsManager({ nutriId, nutriName }) {
  const confirmDelete = useConfirmDelete();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'open' | 'block' | null
  const [form, setForm] = useState({ date: '', times: '', full_day: true, start: '', end: '' });

  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/admin/availability/${nutriId}/exceptions`); setItems(data || []); }
    catch { toast.error('Erro ao carregar exceções'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [nutriId]);

  const reset = () => { setMode(null); setForm({ date: '', times: '', full_day: true, start: '', end: '' }); };

  const submit = async () => {
    if (!form.date) { toast.error('Indica a data'); return; }
    try {
      let body;
      if (mode === 'open') {
        const times = form.times.split(',').map(s => s.trim()).filter(Boolean);
        if (!times.length) { toast.error('Indica pelo menos uma hora'); return; }
        body = { type: 'open', date: form.date, times };
      } else {
        body = { type: 'block', date: form.date, full_day: form.full_day };
        if (!form.full_day) { body.start = form.start; body.end = form.end; }
      }
      await axios.post(`${API}/api/admin/availability/${nutriId}/exceptions`, body);
      toast.success(mode === 'open' ? 'Abertura extra adicionada' : 'Bloqueio adicionado');
      reset(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar'); }
  };

  const remove = async (id) => {
    const ex = items.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a exceção de disponibilidade de ${ex ? fmtExcDate(ex.date) : ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/admin/availability/${nutriId}/exceptions/${id}`); load(); }
    catch { toast.error('Erro ao remover'); }
  };

  return (
    <div className="pt-3 mt-3 border-t border-brand-border/50" data-testid={`exceptions-${nutriId}`}>
      <p className="text-[11px] font-medium text-brand-text mb-2">Exceções de disponibilidade</p>
      <p className="text-[10px] text-brand-muted mb-2">Aberturas extra num dia específico ou bloqueios pontuais. Os bloqueios impedem novas marcações mas nunca apagam consultas já marcadas.</p>

      {loading ? <p className="text-[11px] text-brand-muted">A carregar...</p> : (
        <div className="space-y-1.5 mb-3">
          {items.length === 0 && <p className="text-[11px] text-brand-muted">Sem exceções futuras.</p>}
          {items.map(ex => (
            <div key={ex.id} className="flex items-center justify-between gap-2 bg-brand-bg rounded-lg px-3 py-1.5" data-testid={`exc-item-${ex.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                {ex.type === 'open'
                  ? <CalendarPlus size={14} className="text-brand-green flex-shrink-0" />
                  : <Prohibit size={14} className="text-[#b91c1c] flex-shrink-0" />}
                <span className="text-[11px] text-brand-text truncate">
                  <strong>{fmtExcDate(ex.date)}</strong>
                  {ex.type === 'open'
                    ? ` · abertura: ${(ex.times || []).join(', ')}`
                    : ex.full_day ? ' · bloqueado (dia inteiro)' : ` · bloqueado ${ex.start}–${ex.end}`}
                </span>
              </div>
              <button onClick={() => remove(ex.id)} className="text-[#b91c1c] hover:opacity-70 flex-shrink-0" data-testid={`exc-delete-${ex.id}`}><Trash size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {!mode ? (
        <div className="flex gap-2">
          <button onClick={() => setMode('open')} className="flex items-center gap-1 text-[11px] text-brand-green border border-brand-green rounded-full px-3 py-1" data-testid={`add-open-${nutriId}`}><Plus size={12} /> Abertura extra</button>
          <button onClick={() => setMode('block')} className="flex items-center gap-1 text-[11px] text-[#b91c1c] border border-[#b91c1c] rounded-full px-3 py-1" data-testid={`add-block-${nutriId}`}><Plus size={12} /> Bloqueio</button>
        </div>
      ) : (
        <div className="bg-brand-bg rounded-xl p-3 space-y-2" data-testid={`exc-form-${nutriId}`}>
          <div className="flex flex-wrap items-end gap-3">
            <div><label className="text-[10px] text-brand-muted mb-0.5 block">Data</label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-lg border-brand-border h-9 text-sm" data-testid={`exc-date-${nutriId}`} /></div>
            {mode === 'open' && (
              <div className="flex-1 min-w-[180px]"><label className="text-[10px] text-brand-muted mb-0.5 block">Horas (separadas por vírgula)</label><Input value={form.times} onChange={e => setForm({ ...form, times: e.target.value })} placeholder="09:00, 10:00" className="rounded-lg border-brand-border h-9 text-sm" data-testid={`exc-times-${nutriId}`} /></div>
            )}
            {mode === 'block' && (
              <>
                <label className="flex items-center gap-1.5 text-[11px] text-brand-text pb-2"><input type="checkbox" checked={form.full_day} onChange={e => setForm({ ...form, full_day: e.target.checked })} data-testid={`exc-fullday-${nutriId}`} /> Dia inteiro</label>
                {!form.full_day && (
                  <>
                    <div><label className="text-[10px] text-brand-muted mb-0.5 block">Início</label><Input type="time" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} className="rounded-lg border-brand-border h-9 text-sm" data-testid={`exc-start-${nutriId}`} /></div>
                    <div><label className="text-[10px] text-brand-muted mb-0.5 block">Fim</label><Input type="time" value={form.end} onChange={e => setForm({ ...form, end: e.target.value })} className="rounded-lg border-brand-border h-9 text-sm" data-testid={`exc-end-${nutriId}`} /></div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={submit} className="flex items-center gap-1 bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium" data-testid={`exc-save-${nutriId}`}><FloppyDisk size={12} /> Adicionar</button>
            <button onClick={reset} className="text-xs text-brand-muted px-3" data-testid={`exc-cancel-${nutriId}`}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DisponibilidadePage() {
  useRequireAdmin();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/admin/availability`); setRows(data); }
    catch { toast.error('Erro ao carregar'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setSlot = (i, wd, val) => setRows(r => r.map((row, idx) => idx === i ? { ...row, weekday_slots: { ...row.weekday_slots, [wd]: val } } : row));
  const setBw = (i, field, val) => setRows(r => r.map((row, idx) => idx === i ? { ...row, biweekly_saturday: { ...row.biweekly_saturday, [field]: val } } : row));
  const setField = (i, field, val) => setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  const setTimes = (i, wd, str) => setRows(r => r.map((row, idx) => idx === i ? { ...row, weekday_times: { ...row.weekday_times, [wd]: str.split(',').map(s => s.trim()).filter(Boolean) } } : row));
  const setBwTimes = (i, str) => setRows(r => r.map((row, idx) => idx === i ? { ...row, biweekly_saturday: { ...row.biweekly_saturday, times: str.split(',').map(s => s.trim()).filter(Boolean) } } : row));

  const save = async (row) => {
    try {
      const weekday_slots = {};
      Object.entries(row.weekday_slots || {}).forEach(([k, v]) => { const n = parseInt(v); if (n > 0) weekday_slots[k] = n; });
      await axios.put(`${API}/api/admin/availability/${row.nutri_id}`, {
        weekday_slots,
        weekday_times: row.weekday_times || {},
        biweekly_saturday: { enabled: !!row.biweekly_saturday?.enabled, slots: parseInt(row.biweekly_saturday?.slots) || 0, anchor_date: row.biweekly_saturday?.anchor_date || '', times: row.biweekly_saturday?.times || [] },
        booking_price: parseFloat(row.booking_price) || 0,
        slot_duration: parseInt(row.slot_duration) || 60,
        working_days_per_week: parseInt(row.working_days_per_week) || 0,
        custom_slug: row.custom_slug || '',
        welcome_message: row.welcome_message || '',
        color: row.color || '#5B8C7B',
      });
      toast.success(`Disponibilidade de ${row.name} guardada`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar'); }
  };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in max-w-4xl" data-testid="disponibilidade-page">
      <div className="mb-6">
        <h2 className="font-heading font-medium text-2xl text-brand-text flex items-center gap-2"><CalendarCheck size={22} weight="duotone" className="text-brand-gold-hover" /> Disponibilidade</h2>
        <p className="text-sm text-brand-muted">Vagas de consulta por dia da semana, por profissional. Usado na Taxa de ocupação.</p>
      </div>
      <div className="space-y-4">
        {rows.map((row, i) => (
          <div key={row.nutri_id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`avail-${row.nutri_id}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-sm text-brand-text">{row.name}</h3>
              <button onClick={() => save(row)} className="flex items-center gap-1.5 bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium" data-testid={`save-avail-${row.nutri_id}`}><FloppyDisk size={13} /> Guardar</button>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-3">
              {WEEKDAYS.map(([wd, label]) => (
                <div key={wd}>
                  <label className="text-[10px] text-brand-muted mb-0.5 block text-center">{label}</label>
                  <Input type="number" min="0" value={row.weekday_slots?.[wd] ?? ''} onChange={e => setSlot(i, wd, e.target.value)} className="rounded-lg border-brand-border text-center h-9 text-sm" data-testid={`slot-${row.nutri_id}-${wd}`} />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-brand-border/50">
              <label className="flex items-center gap-1.5 text-xs text-brand-text">
                <input type="checkbox" checked={!!row.biweekly_saturday?.enabled} onChange={e => setBw(i, 'enabled', e.target.checked)} data-testid={`bw-enabled-${row.nutri_id}`} /> Sábados quinzenais
              </label>
              {row.biweekly_saturday?.enabled && (
                <>
                  <div><label className="text-[10px] text-brand-muted mb-0.5 block">Vagas/sábado</label><Input type="number" min="0" value={row.biweekly_saturday?.slots ?? ''} onChange={e => setBw(i, 'slots', e.target.value)} className="rounded-lg border-brand-border h-9 text-sm w-24" data-testid={`bw-slots-${row.nutri_id}`} /></div>
                  <div><label className="text-[10px] text-brand-muted mb-0.5 block">Sábado de referência</label><Input type="date" value={row.biweekly_saturday?.anchor_date || ''} onChange={e => setBw(i, 'anchor_date', e.target.value)} className="rounded-lg border-brand-border h-9 text-sm" data-testid={`bw-anchor-${row.nutri_id}`} /></div>
                  <div className="w-full"><label className="text-[10px] text-brand-muted mb-0.5 block">Horas sábado (separadas por vírgula)</label><Input value={(row.biweekly_saturday?.times || []).join(', ')} onChange={e => setBwTimes(i, e.target.value)} placeholder="09:00, 10:00, 11:00, 12:00" className="rounded-lg border-brand-border h-9 text-sm" data-testid={`bw-times-${row.nutri_id}`} /></div>
                </>
              )}
            </div>

            {/* Agendamento público */}
            <div className="pt-3 mt-3 border-t border-brand-border/50">
              <p className="text-[11px] font-medium text-brand-text mb-2">Agendamento público (/agendarconsulta)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-3">
                {WEEKDAYS.filter(([wd]) => wd !== '6' && wd !== '0').map(([wd, label]) => (
                  <div key={wd}>
                    <label className="text-[10px] text-brand-muted mb-0.5 block">Horas {label} (ex: 09:30, 10:30)</label>
                    <Input value={(row.weekday_times?.[wd] || []).join(', ')} onChange={e => setTimes(i, wd, e.target.value)} placeholder="09:30, 10:30, 11:30" className="rounded-lg border-brand-border h-9 text-sm" data-testid={`times-${row.nutri_id}-${wd}`} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><label className="text-[10px] text-brand-muted mb-0.5 block">Preço 1ª consulta (€)</label><Input type="number" min="0" step="0.5" value={row.booking_price ?? ''} onChange={e => setField(i, 'booking_price', e.target.value)} className="rounded-lg border-brand-border h-9 text-sm" data-testid={`price-${row.nutri_id}`} /></div>
                <div><label className="text-[10px] text-brand-muted mb-0.5 block">Duração (min)</label><Input type="number" min="15" step="15" value={row.slot_duration ?? 60} onChange={e => setField(i, 'slot_duration', e.target.value)} className="rounded-lg border-brand-border h-9 text-sm" data-testid={`duration-${row.nutri_id}`} /></div>
                <div><label className="text-[10px] text-brand-muted mb-0.5 block">Dias de trabalho/semana</label><Input type="number" min="1" max="5" value={row.working_days_per_week ?? ''} onChange={e => setField(i, 'working_days_per_week', e.target.value)} placeholder="1–5" className="rounded-lg border-brand-border h-9 text-sm" data-testid={`workdays-${row.nutri_id}`} /></div>
                <div><label className="text-[10px] text-brand-muted mb-0.5 block">Cor</label><Input type="color" value={row.color || '#5B8C7B'} onChange={e => setField(i, 'color', e.target.value)} className="rounded-lg border-brand-border h-9 p-1" data-testid={`color-${row.nutri_id}`} /></div>
              </div>
              <div className="mt-2"><label className="text-[10px] text-brand-muted mb-0.5 block">Mensagem de boas-vindas</label><Input value={row.welcome_message || ''} onChange={e => setField(i, 'welcome_message', e.target.value)} placeholder="Bem-vinda! Marca aqui a tua 1ª consulta." className="rounded-lg border-brand-border h-9 text-sm" data-testid={`welcome-${row.nutri_id}`} /></div>
              <div className="mt-2">
                <label className="text-[10px] text-brand-muted mb-0.5 block">Link personalizado (slug)</label>
                <Input value={row.custom_slug || ''} onChange={e => setField(i, 'custom_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="ex: ana ou filipa" className="rounded-lg border-brand-border h-9 text-sm" data-testid={`slug-${row.nutri_id}`} />
                {row.custom_slug && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <code className="text-[11px] text-brand-green bg-brand-bg px-2 py-1 rounded-lg flex-1 truncate" data-testid={`slug-url-${row.nutri_id}`}>{bookingBase}/agendarconsulta/{row.custom_slug}</code>
                    <button type="button" onClick={() => { navigator.clipboard?.writeText(`${bookingBase}/agendarconsulta/${row.custom_slug}`); toast.success('Link copiado'); }} className="text-[11px] text-brand-green hover:underline flex items-center gap-1" data-testid={`slug-copy-${row.nutri_id}`}><Copy size={12} /> Copiar</button>
                  </div>
                )}
              </div>
            </div>
            <ExceptionsManager nutriId={row.nutri_id} nutriName={row.name} />
          </div>
        ))}
      </div>
    </div>
  );
}
